import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactCountryFlag from 'react-country-flag';
import './PurchaseCredits.css';
import Title from 'antd/lib/typography/Title';
import { FormattedMessage, useIntl } from 'react-intl';
import { Select, Spin, Alert } from 'antd';
import { Link } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import { requestCreditPackages } from '../../../state/ui/purchase/purchaseCreditsUiActions';
import { selectCreditPackagesRequest, selectLoadedCreditPackages } from '../../../state/ui/purchase/purchaseCreditsUiReducer';
import { CURRENCIES } from '../../../utility/via-util';
import CreditPackages from './CreditPackages';
import { filterCreditPackages } from './purchase-util';
import { requestUserGroupDetails } from '../../../state/ui/configuration/company/companyUiActions';
import routeLocations from '../../../utility/route-locations';
import { selectPurchaseLimitation, purchaseLimitations } from '../../../state/domain/packages/packageReducer';


const ShowWarning = ({ emailDetails, addressDetails }) => (
  <ul>
    {
      emailDetails ? (
        <li>
          {emailDetails.emailMessage}
          &nbsp;
          {emailDetails.emailLink}
        </li>
      )
        : ''
    }
    {
      addressDetails ? (
        <li>
          {addressDetails.addressMessage}
          {addressDetails.addressLink}
        </li>
      )
        : ''
    }
  </ul>
);
ShowWarning.propTypes = {
  emailDetails: PropTypes.shape({
    emailMessage: PropTypes.node,
    emailLink: PropTypes.node,
  }),
  addressDetails: PropTypes.shape({
    addressMessage: PropTypes.node,
    addressLink: PropTypes.node,
  }),
};

ShowWarning.defaultProps = {
  emailDetails: null,
  addressDetails: null,
};


const EmailLink = () => (
  <Link to={routeLocations.configure_my_details}>
    <FormattedMessage id="reviewMyDetails" />
  </Link>
);

const EmailMessage = () => {
  const intl = useIntl();
  return (intl.formatMessage({ id: 'yourEmailUnverified' }));
};

const emailDetails = {
  emailMessage: <EmailMessage />,
  emailLink: <EmailLink />,
};

const AddressLink = () => (
  <Link to={routeLocations.configure_contact_details}>
    <FormattedMessage id="editCompanyDetails" />
  </Link>
);

const AddressMessage = () => {
  const intl = useIntl();
  return (intl.formatMessage({ id: 'noCompanyAddress' }));
};

const AddressMessageNoLink = () => {
  const intl = useIntl();
  return (intl.formatMessage({ id: 'noCompanyAddressContact' }));
};

const addressDetails = {
  addressMessage: <AddressMessage />,
  addressLink: <AddressLink />,
};

const addressDetailsNoLink = {
  addressMessage: <AddressMessageNoLink />,
};

const PurchaseLimitations = ({ purchaseLimitation }) => {
  // email unverified - show link;  company address missing - show link
  if (purchaseLimitation === purchaseLimitations.emailAddressMissingWithLinks) {
    return (<ShowWarning emailDetails={emailDetails} addressDetails={addressDetails} />);
  }

  // email unverified - show link; company address available
  if (purchaseLimitation === purchaseLimitations.emailUnverifiedWithLink) { return (<ShowWarning emailDetails={emailDetails} />); }

  // email verified; company address missing - show link
  if (purchaseLimitation === purchaseLimitations.addressMissingWithLink) { return (<ShowWarning addressDetails={addressDetails} />); }

  // email unverified - show link; company address missing - DONT show link; SHOW different message as user does not have access to amend the address
  if (purchaseLimitation === purchaseLimitations.emailAddressMissingEmailLink) {
    return (<ShowWarning emailDetails={emailDetails} addressDetails={addressDetailsNoLink} />);
  }

  // email verified; company address missing - DONT show link; SHOW different message as user does not have access to amend the address
  if (purchaseLimitation === purchaseLimitations.addressMissingNoLink) { return (<ShowWarning addressDetails={addressDetailsNoLink} />); }

  return null;
};

PurchaseLimitations.propTypes = {
  purchaseLimitation: PropTypes.string,
};

PurchaseLimitations.defaultProps = {
  purchaseLimitation: purchaseLimitations,
};

/**
 * A component to display before the page has started loading.
 */
function PurchaseCreditsSkeleton() {
  return (
    <div className="via-centre">
      <div className="purchase-credits-title">
        <Spin className="via-centre" />
      </div>
    </div>
  );
}

/**
 * The page for viewing and purchasing credit packages.
 */
export default function PurchaseCredits() {
  // Redux State
  const creditPackagesRequest = useSelector(selectCreditPackagesRequest);
  const isLoading = creditPackagesRequest && creditPackagesRequest.isLoading;
  const creditPackages = useSelector(selectLoadedCreditPackages);
  const currencies = creditPackages ? [...new Set(creditPackages.map(p => p.currencyCode))] : [];

  // Redux Actions
  const dispatch = useDispatch();
  const loadPackages = useCallback(() => dispatch(requestCreditPackages()), [dispatch]);
  const loadUserGroup = useCallback(() => dispatch(requestUserGroupDetails()), [dispatch]);

  // React State
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState();

  // Load packages on page entry.
  useEffect(() => {
    loadPackages();
    loadUserGroup();
  }, [loadPackages, loadUserGroup]);

  // Default the currency code once we load all the available packages.
  useEffect(() => {
    if (!creditPackages || creditPackages.length <= 0) return;
    setSelectedCurrencyCode(creditPackages[0].currencyCode);
  }, [creditPackages, setSelectedCurrencyCode]);

  const purchaseLimitation = useSelector(selectPurchaseLimitation);

  const ShowWarningNotification = () => {
    if (!purchaseLimitation) return null;

    return (
      <Alert
        message={<FormattedMessage id="purchaseDisallowed" />}
        type="warning"
        description={<PurchaseLimitations purchaseLimitation={purchaseLimitation} />}
        showIcon
      />
    );
  };

  // Render: Hasn't started loading yet.
  if (!creditPackagesRequest || creditPackagesRequest.isLoading) return <PurchaseCreditsSkeleton />;


  // Render: Default, show title + packages.
  return (
    <div className="purchase-credits">
      <div className="purchase-credits-title via-centre">
        <Title level={2} className="purchase-credits-header"><FormattedMessage id="purchaseInspections" /></Title>
        <div className="purchase-credits-summary">
          <FormattedMessage id="creditConsumptionDesc" />
        </div>
        <div className="purchase-credits-legal-section">
          <FormattedMessage id="creditExpiryDesc" />
        </div>
        <div className="purchase-credits-currency">
          <Select value={selectedCurrencyCode} onSelect={setSelectedCurrencyCode} loading={isLoading} style={{ visibility: !selectedCurrencyCode && 'hidden' }}>
            { currencies.map(code => (
              <Select.Option key={code}>
                <ReactCountryFlag svg code={CURRENCIES[code].flag} />
                {` (${CURRENCIES[code].symbol}) ${CURRENCIES[code].description}`}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="purchase-credits-section">
        <div className="via-centre">
          <ShowWarningNotification />
        </div>
        <CreditPackages
          creditPackages={filterCreditPackages(creditPackages, selectedCurrencyCode)}
          disablePackageSelection={!!purchaseLimitation}
        />
      </div>
    </div>
  );
}
